import { ApiCustomField, ApiCustomFieldValue } from "@/types/custom-field.types.ts";
import { ApiAction } from "@/types/action.types.ts";
import { ApiAIProcessingStatus } from "@/types/ai/ai.types.ts";
import { ApiSorting, ApiSortingDirection } from "@/types/api.types.ts";
import { ApiCommentVisibilities } from "@/types/comment.types.ts";
import { ApiEntityTypes, ApiFile, ApiPartialDate } from "@/types/common.types.ts";
import { ApiApplicantDataProtection } from "@/types/data-protection.types.ts";
import { ApiEmployee, ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { ApiFilterBase, ApiPagedData } from "@skailer-com/uikit/core";
import { ApiApplicantCV } from "@/types/recruit/applicant.cv.types.ts";
import { ApiCandidateDisqualificationReason } from "@/types/recruit/disqualification-reason.types.ts";
import { ApiJobShort } from "@/types/recruit/job.types.ts";
import { ApiLabel } from "@/types/recruit/label.types.ts";
import { ApiPipelineShort } from "@/types/recruit/pipeline.types.ts";
import { SourceTypes } from "@/types/recruit/source.types.ts";
import { ApiPipelineStageShort } from "@/types/recruit/stages.types.ts";
import { AvatarState, TagVariants } from "@skailer-com/uikit";
import i18n from "i18next";

export enum ContactType {
  email = "email",
  phone = "phone",
  telegram = "telegram",
  link = "link",
  other = "other",
}

export interface ApiApplicantContact {
  value: string;
  type: ContactType;
  label?: string | null;
}

export interface ApiApplicantContacts {
  phone?: string;
  email?: string;
  telegram_username?: string;
}

export interface ApiApplicantPosition {
  position: string | null;
  company: string | null;
}

export interface ApiApplicantSource {
  id: number;
  source_type: SourceTypes;
  title: string;
}

export interface ApiApplicantBase {
  id: number;
  first_name: string;
  last_name?: string;
  middle_name?: string;
  photo_url?: string;
}

export interface ApiApplicantShort extends ApiApplicantBase {
  location?: string;
  contacts: ApiApplicantContacts;
  additional_contacts: ApiApplicantContact[];
  current_position: ApiApplicantPosition;
  salary?: string;
  birth_date?: ApiPartialDate;
  photo_url?: string;
  resume?: string | null;
  source_id: number;
  stage_is_overdue: boolean;

  last_action?: ApiAction;
  candidates: ApiCandidate[];

  datetime_created_at: Date;

  data_protection?: ApiApplicantDataProtection[];
  last_or_unread_comment_data?: ApiApplicantCommentData;

  have_unread_candidates?: boolean;
  have_unread_mentions?: boolean;

  potential_threat?: ApiApplicantThreat[];
}

export enum ApiApplicantThreatType {
  mixed_characters = "mixed_characters",
  hidden_ai_instructions = "hidden_ai_instructions",
  ai_generated_cv = "ai_generated_cv",
}

export interface ApiApplicantThreat {
  type: ApiApplicantThreatType;
}

export interface ApiApplicantCommentData {
  author: ApiEmployeeShort;
  mentioned_employees: ApiEmployeeShort[];
  notification?: ApiCommentNotification;
  text: string;
  visibility: ApiCommentVisibilities;
  files: ApiFile[];
  entity_type: ApiEntityTypes;
  edited: boolean;
  entity_id: number;
}

export interface ApiCommentNotification {
  id: number;
  uuid: string;
}

export interface ApiApplicantSavePayload {
  custom_fields?: Record<string, ApiCustomFieldValue>;
  photo_file_uuid?: string;
  first_name: string;
  last_name?: string;
  middle_name?: string;
  location?: string;
  contacts: ApiApplicantContacts;
  additional_contacts: ApiApplicantContact[];
  current_position: ApiApplicantPosition;
  salary?: string;
  birth_date?: ApiPartialDate;
  source_id?: number;
  resume?: string;
  cv_ids?: number[];
}

export interface ApiApplicantCreatePayload {
  new_applicant: ApiApplicantSavePayload;
}

export interface ApiApplicantFilter extends ApiFilterBase {
  location?: string;
  company?: string;
  text?: string;
  position?: string;
  date_created_from?: Date;
  date_created_to?: Date;
  source_ids?: number[];
  pipeline_stage_ids?: number[];
  job_ids?: number[];
  disqualification_reason_ids?: number[];
  reserved?: boolean;
  disqualified?: boolean;
  applicant_ids?: number[];
  exclude_applicant_ids?: number[];
  date_last_candidate_action_from?: Date;
  date_last_candidate_action_to?: Date;
  label_ids?: number[];
  comment?: string;
  date_applicant_birth_from?: Date;
  date_applicant_birth_to?: Date;
  recruiter_ids?: number[];
  mention_state?: ApiApplicantMentionState | undefined;
  stakeholder_ids?: number[];
  action_employee_ids?: number[];
  author_ids?: number[];
  sorting: ApiSorting;
  assigned_by_ids?: number[];
}

export interface ApiApplicantRecommendationFilter extends ApiFilterBase {
  job_id: number;
}

export enum ApiApplicantMentionState {
  any = "any",
  unread = "unread",
}

export interface ApiApplicantMentions {
  unread_mentions_count: number;
  authors: ApiEmployeeShort[];
}

export interface ApiApplicantParsed {
  applicant: ApiApplicantSavePayload;
  files: ApiFile[];
}

export interface ApiApplicant extends ApiApplicantShort {
  custom_fields?: ApiCustomField[];
  author: ApiEmployeeShort | null;
  cvs: ApiApplicantCV[];
  labels: ApiLabel[];
  source: ApiApplicantSource;
  color_palette: string[];
  employee?: ApiEmployee | null;

  similar_applicants: ApiApplicantShort[];
  ignored_applicants: ApiApplicantShort[];
}

export interface ApiCandidateShort {
  id: number;
  job_id: number;
}

export enum ApiCandidateAIScoreMark {
  excellent = "excellent",
  good = "good",
  average = "average",
  poor = "poor",
}

export interface ApiSkillEvaluation {
  title: string;
  mark: ApiCandidateAIScoreMark;
}

export interface ApiCandidateAiScore {
  ai_score_edited_by: ApiEmployeeShort | null;
  // datetime_ai_score_evaluated_at: Date;
  // ai_score_status: SKSurveyAIProcessingStatus | None;
  ai_score_value: number;
  ai_score_mark: ApiCandidateAIScoreMark;
  ai_score_summary: string;
  ai_score_skills_evaluation: ApiSkillEvaluation[];
}

export interface ApiCandidate {
  id: number;
  datetime_created_at: string;
  job: ApiJobShort;
  pipeline: ApiPipelineShort;
  pipeline_stage: ApiPipelineStageShort;
  reserved?: boolean | false;
  disqualification_reason?: ApiCandidateDisqualificationReason | null;

  datetime_stage_deadline?: Date;
  business_days_on_stage?: number;
  stage_is_overdue: boolean;
  stage_time_limit: number;
  last_action?: ApiAction;
  ai_score_status: ApiAIProcessingStatus;
  ai_score: ApiCandidateAiScore | null;
}

export const ApplicantShortEmpty: ApiApplicantShort = {
  id: 0,
  first_name: "",
  last_name: "",
  middle_name: "",
  location: "",
  contacts: {},
  additional_contacts: [],
  current_position: {
    position: "",
    company: "",
  },
  salary: "",
  birth_date: undefined,
  photo_url: "",
  resume: "",
  source_id: 0,
  stage_is_overdue: false,
  datetime_created_at: new Date(),
  candidates: [],
};

export interface ApiApplicantSearchData extends ApiPagedData<ApiApplicantShort> {
  jobs: ApiJobShort[];
}

export enum ApplicantCVSupportedFileTypes {
  PDF = "application/pdf",
  DOC = "application/msword",
  DOCX = "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  RTF = "application/rtf",
  ODT = "application/vnd.oasis.opendocument.text",
}

export enum ApplicantSortField {
  candidate_created_at = "candidate_created_at",
  applicant_created_at = "applicant_created_at",
  candidate_ai_score = "candidate_ai_score",
  last_action_at = "last_action_at",
  search_similarity = "search_similarity",
}

export const ApplicantFilterDefault: ApiApplicantFilter = {
  disqualification_reason_ids: [],
  job_ids: [],
  page_number: 1,
  pipeline_stage_ids: [],
  reserved: undefined,
  disqualified: undefined,
  source_ids: [],
  date_last_candidate_action_from: undefined,
  date_last_candidate_action_to: undefined,
  label_ids: [],
  recruiter_ids: [],
  date_applicant_birth_from: undefined,
  date_applicant_birth_to: undefined,
  mention_state: undefined,
  stakeholder_ids: [],
  action_employee_ids: [],
  author_ids: [],
  sorting: {
    fields: [{ field: ApplicantSortField.last_action_at, direction: ApiSortingDirection.desc }],
  },
};

export const getCandidateScoreMarkTitle = (mark: ApiCandidateAIScoreMark): string => {
  switch (mark) {
    case ApiCandidateAIScoreMark.poor:
      return i18n.t("core:score_mark_poor");
    case ApiCandidateAIScoreMark.average:
      return i18n.t("core:score_mark_average");
    case ApiCandidateAIScoreMark.good:
      return i18n.t("core:score_mark_good");
    case ApiCandidateAIScoreMark.excellent:
      return i18n.t("core:score_mark_excellent");
    default:
      return "-";
  }
};

export const getCandidateScoreCircleEmoji = (mark: ApiCandidateAIScoreMark): string => {
  switch (mark) {
    case ApiCandidateAIScoreMark.poor:
      return ":red_circle:";
    case ApiCandidateAIScoreMark.average:
      return ":large_orange_circle:";
    case ApiCandidateAIScoreMark.good:
      return ":large_green_circle:";
    case ApiCandidateAIScoreMark.excellent:
      return ":large_purple_circle:";
    default:
      return ":white_circle:";
  }
};

export const getCandidateScoreMarkVariant = (mark: ApiCandidateAIScoreMark): TagVariants => {
  switch (mark) {
    case ApiCandidateAIScoreMark.poor:
      return "danger";
    case ApiCandidateAIScoreMark.average:
      return "warning";
    case ApiCandidateAIScoreMark.good:
      return "positive";
    case ApiCandidateAIScoreMark.excellent:
      return "violet";
  }
};

export const getCandidateScoreMarkAvatarState = (mark: ApiCandidateAIScoreMark): AvatarState => {
  switch (mark) {
    case ApiCandidateAIScoreMark.poor:
      return "danger";
    case ApiCandidateAIScoreMark.average:
      return "warning";
    case ApiCandidateAIScoreMark.good:
      return "positive";
    case ApiCandidateAIScoreMark.excellent:
      return "violet";
  }
};
